import React, { useEffect, useState, useMemo, useCallback } from "react";
import { HorizontalChart } from "@components/HorizontalChart";
import { Graph } from "@components/HorizontalChart/chart.types";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { ExpandableTable } from "@components/ExpandebleTable";
import { RangeCard } from "@components/AdSpend/RangeCard";
import Arrow from "@assets/icons/Arrow";
import {
  customerJourneyColumns,
  customerJourneyData,
  generateAudiencePaths,
} from "./mock";

interface CustomerJourneyMapProps {
  isWhatIf?: boolean;
}

export const CustomerJourneyMap: React.FC<CustomerJourneyMapProps> = ({
  isWhatIf,
}) => {
  const [chartData, setChartData] = useState<Graph>(customerJourneyData);
  const [isExposed, setIsExposed] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  // Ad Spend card states
  const [adSpend, setAdSpend] = useState(0);
  const [adBids, setAdBids] = useState(0);
  const [timeInBudget, setTimeInBudget] = useState(0);
  const [topOfSearch, setTopOfSearch] = useState(0);
  const [productPages, setProductPages] = useState(0);
  const [restOfSearch, setRestOfSearch] = useState(0);
  const [isAdSpendAdvancedOpen, setIsAdSpendAdvancedOpen] = useState(false);

  // DSP Spend card states
  const [dspSpend, setDspSpend] = useState(0);
  const [dspRetargeting, setDspRetargeting] = useState(0);
  const [isDspSpendAdvancedOpen, setIsDspSpendAdvancedOpen] = useState(false);

  // Generate base audience sizes from journey data
  const baseAudienceSizes = useMemo(() => {
    return generateAudiencePaths(customerJourneyData);
  }, []);

  const calculateAudienceSize = (
    basePath: string,
    baseSize: number,
  ): number => {
    let adjustedSize = baseSize;

    // Only apply modifiers if this is the selected path or no path is selected
    if (!selectedPath || selectedPath === basePath) {
      if (basePath.includes("Attribution sales")) {
        adjustedSize = baseSize * (1 + adSpend / 100);
      }

      if (basePath.includes("DSP")) {
        adjustedSize = baseSize * (1 + dspSpend / 100);
      }

      if (
        basePath.includes("Add to cart") ||
        basePath.includes("Add-to-wish-list") ||
        basePath.includes("Add-to-gift-list")
      ) {
        adjustedSize = adjustedSize * (1 + dspRetargeting / 100);
      }
    }

    return Math.round(adjustedSize);
  };

  const parsePathToNodes = (path: string): string[] => {
    return path.split(" → ").map((node) => node.trim());
  };

  const customerJourneyTableData = useMemo(() => {
    console.log(selectedNode, "selectedNode");
    if (selectedNode) {
      return Object.entries(baseAudienceSizes)
        .map(([path, baseSize], index) => ({
          key: (index + 1).toString(),
          path,
          audienceSize: calculateAudienceSize(path, baseSize),
          isSelected: selectedPath === path,
        }))
        .filter((item) => item.path.includes(selectedNode));
    }

    return Object.entries(baseAudienceSizes).map(([path, baseSize], index) => ({
      key: (index + 1).toString(),
      path,
      audienceSize: calculateAudienceSize(path, baseSize),
      isSelected: selectedPath === path,
    }));
  }, [
    baseAudienceSizes,
    adSpend,
    dspSpend,
    dspRetargeting,
    selectedPath,
    selectedNode,
  ]);

  const updateChartData = useCallback(() => {
    const updatedNodes = customerJourneyData.nodes.map((node) => {
      let adjustedValue = node.value;

      // Only apply modifiers if no path is selected or if the node is part of the selected path
      if (!selectedPath) {
        // Apply to all nodes when no specific path is selected
        if (node.name === "Attribution sales") {
          adjustedValue = node.value * (1 + adSpend / 100);
        } else if (node.name === "DSP") {
          adjustedValue = node.value * (1 + dspSpend / 100);
        } else if (node.name === "Search Volume") {
          adjustedValue = node.value * (1 + dspSpend / 100);
        } else if (
          node.name.includes("Add to cart") ||
          node.name.includes("Add-to-wish-list") ||
          node.name.includes("Add-to-gift-list")
        ) {
          adjustedValue = node.value * (1 + dspRetargeting / 100);
        }
      } else {
        // Only apply to nodes that are part of the selected path
        const pathNodes = parsePathToNodes(selectedPath);
        if (pathNodes.includes(node.name)) {
          if (node.name === "Attribution sales") {
            adjustedValue = node.value * (1 + adSpend / 100);
          } else if (node.name === "DSP") {
            adjustedValue = node.value * (1 + dspSpend / 100);
          } else if (node.name === "Search Volume") {
            adjustedValue = node.value * (1 + dspSpend / 100);
          } else if (
            node.name.includes("Add to cart") ||
            node.name.includes("Add-to-wish-list") ||
            node.name.includes("Add-to-gift-list")
          ) {
            adjustedValue = node.value * (1 + dspRetargeting / 100);
          }
        }
      }

      return { ...node, value: Math.round(adjustedValue) };
    });

    const updatedLinks = customerJourneyData.links.map((link) => {
      let adjustedValue = link.value;

      // Only apply modifiers if no path is selected or if the link is part of the selected path
      if (!selectedPath) {
        // Apply to all links when no specific path is selected
        if (link.source === "Attribution sales") {
          adjustedValue = link.value * (1 + adSpend / 100);
        } else if (link.source === "DSP") {
          adjustedValue = link.value * (1 + dspSpend / 100);
        } else if (link.source === "Search Volume") {
          adjustedValue = link.value * (1 + dspSpend / 100);
        } else if (
          link.source.includes("Add to cart") ||
          link.source.includes("Add-to-wish-list") ||
          link.source.includes("Add-to-gift-list")
        ) {
          adjustedValue = link.value * (1 + dspRetargeting / 100);
        }
      } else {
        // Only apply to links that are part of the selected path
        const pathNodes = parsePathToNodes(selectedPath);
        if (
          pathNodes.includes(link.source) &&
          pathNodes.includes(link.target)
        ) {
          if (link.source === "Attribution sales") {
            adjustedValue = link.value * (1 + adSpend / 100);
          } else if (link.source === "DSP") {
            adjustedValue = link.value * (1 + dspSpend / 100);
          } else if (link.source === "Search Volume") {
            adjustedValue = link.value * (1 + dspSpend / 100);
          } else if (
            link.source.includes("Add to cart") ||
            link.source.includes("Add-to-wish-list") ||
            link.source.includes("Add-to-gift-list")
          ) {
            adjustedValue = link.value * (1 + dspRetargeting / 100);
          }
        }
      }

      return { ...link, value: Math.round(adjustedValue) };
    });

    const newChartData = { nodes: updatedNodes, links: updatedLinks };

    // Preserve highlighting based on current selection
    if (selectedPath) {
      const pathNodes = parsePathToNodes(selectedPath);
      applyPathHighlight(newChartData, pathNodes);
    } else if (selectedStep) {
      applyStepHighlight(newChartData, selectedStep);
    } else {
      setChartData(newChartData);
    }
  }, [adSpend, dspSpend, dspRetargeting, selectedPath, selectedStep]);

  // Helper function to apply path highlighting
  const applyPathHighlight = (chartData: Graph, pathNodes: string[]) => {
    const highlightedNodes = chartData.nodes.map((node) => ({
      ...node,
      itemStyle: {
        ...node.itemStyle,
        opacity: pathNodes.includes(node.name) ? 1 : 0.2,
      },
      label: {
        ...node.label,
        color: pathNodes.includes(node.name)
          ? "#fff"
          : "rgba(255, 255, 255, 0.3)",
      },
    }));

    const highlightedLinks = chartData.links.map((link) => {
      const isInPath =
        pathNodes.includes(link.source) && pathNodes.includes(link.target);

      return {
        ...link,
        lineStyle: {
          ...link.lineStyle,
          opacity: isInPath ? 1 : 0.1,
        },
      };
    });

    setChartData({ nodes: highlightedNodes, links: highlightedLinks });
  };

  // Helper function to apply step highlighting
  const applyStepHighlight = (chartData: Graph, stepNumber: number) => {
    const stepNodes = getNodesAtStep(stepNumber);

    // Get all nodes up to and including the selected step
    const allNodesUpToStep: string[] = [];
    for (let i = 1; i <= stepNumber; i++) {
      allNodesUpToStep.push(...getNodesAtStep(i));
    }

    const highlightedNodes = chartData.nodes.map((node) => ({
      ...node,
      itemStyle: {
        ...node.itemStyle,
        opacity: allNodesUpToStep.includes(node.name) ? 1 : 0.2,
      },
      label: {
        ...node.label,
        color: allNodesUpToStep.includes(node.name)
          ? "#fff"
          : "rgba(255, 255, 255, 0.3)",
      },
    }));

    const highlightedLinks = chartData.links.map((link) => {
      // Highlight link if both source and target are in the path up to selected step
      const isInPath =
        allNodesUpToStep.includes(link.source) &&
        allNodesUpToStep.includes(link.target);

      // Or if the link leads to the selected step from previous steps
      const leadsToStep =
        allNodesUpToStep.includes(link.source) &&
        stepNodes.includes(link.target);

      return {
        ...link,
        lineStyle: {
          ...link.lineStyle,
          opacity: isInPath || leadsToStep ? 0.6 : 0.1,
        },
      };
    });

    setChartData({ nodes: highlightedNodes, links: highlightedLinks });
  };

  useEffect(() => {
    updateChartData();
  }, [updateChartData]);

  const customSeriesOptions = {
    nodeAlign: "left",
    nodeGap: 15,
    nodeWidth: 20,
    layoutIterations: 512,
    nodeSort: null,
  };

  const handleRangeReset = (
    setter: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    setter(0);
  };

  const highlightPath = (pathNodes: string[]) => {
    applyPathHighlight(chartData, pathNodes);
  };

  const stepDepthRanges = {
    1: { min: 0, max: 1.5 },
    2: { min: 1.5, max: 3.0 },
    3: { min: 3.0, max: 4.0 },
    4: { min: 4.0, max: 5.5 },
    5: { min: 5.5, max: 7.0 },
  };

  const getNodesAtStep = (stepNumber: number): string[] => {
    const range = stepDepthRanges[stepNumber];
    if (!range) return [];

    return customerJourneyData.nodes
      .filter((node) => node.depth >= range.min && node.depth < range.max)
      .map((node) => node.name);
  };

  const highlightStep = (stepNumber: number) => {
    applyStepHighlight(chartData, stepNumber);
  };

  const handleStepClick = (stepNumber: number) => {
    if (selectedStep === stepNumber) {
      updateChartData();
      setSelectedStep(null);
      setSelectedPath(null);
    } else {
      highlightStep(stepNumber);
      setSelectedStep(stepNumber);
      setSelectedPath(null);
    }
  };

  const resetChartHighlight = () => {
    updateChartData();
    setSelectedPath(null);
    setSelectedStep(null);
  };

  const handleTableRowClick = (record: any) => {
    if (selectedPath === record.path) {
      resetChartHighlight();
    } else {
      const pathNodes = parsePathToNodes(record.path);
      highlightPath(pathNodes);
      setSelectedPath(record.path);
      setSelectedStep(null);
    }
  };

  const shouldShowDspSpend = useMemo(() => {
    if (!selectedPath) return true;
    return (
      selectedPath.includes("DSP") || selectedPath.includes("Search Volume")
    );
  }, [selectedPath]);

  return (
    <div className={styles.customerJourneyMap}>
      <div className={styles.header}>
        <h1 className={styles.title}>Customer Journey Map</h1>
        <div className={styles.settingsButton} aria-label="Column settings">
          <div
            className={classNames(
              styles.exposureType,
              isExposed ? styles.exposed : styles.nonExposed,
            )}
          >
            <button onClick={() => setIsExposed(!isExposed)}>Ad Exposed</button>
            <button onClick={() => setIsExposed(!isExposed)}>
              Non Ad Exposed
            </button>
          </div>
        </div>
      </div>

      {isWhatIf && (
        <div className={styles.whatIfContainer}>
          <div className={styles.tableContainer}>
            <ExpandableTable
              columns={customerJourneyColumns}
              data={customerJourneyTableData}
              className={classNames(
                styles.customerJourneyTable,
                styles.selectableTable,
              )}
              scroll={{ x: 400, y: 600 }}
              onRowClick={handleTableRowClick}
              hideRowSelection={true}
            />
          </div>
          <div className={styles.rangeInputs}>
            {/* Ad Spend Card */}
            <div className={styles.spendCard}>
              <h3>Ad Spend</h3>

              {/* Primary Ad Spend slider - always visible */}
              {!isAdSpendAdvancedOpen && (
                <RangeCard
                  title="Ad Spend"
                  label="%"
                  value={adSpend}
                  onChange={([min, max]) => {
                    const currentValue = min === 0 ? max : min;
                    setAdSpend(currentValue);
                  }}
                  min={-70}
                  max={300}
                  isWhatIf={true}
                  onReset={() => handleRangeReset(setAdSpend)}
                />
              )}

              <div className={styles.advanced}>
                <button
                  className={classNames(
                    styles.advanced__button,
                    isAdSpendAdvancedOpen && styles.active,
                  )}
                  onClick={() => {
                    setIsAdSpendAdvancedOpen(!isAdSpendAdvancedOpen);
                  }}
                >
                  Advanced options <Arrow />
                </button>
              </div>
              {isAdSpendAdvancedOpen && (
                <div className={styles.cards}>
                  <RangeCard
                    title="Ad Spend"
                    label="%"
                    value={adSpend}
                    onChange={([min, max]) => {
                      const currentValue = min === 0 ? max : min;
                      setAdSpend(currentValue);
                    }}
                    min={-70}
                    max={300}
                    isWhatIf={true}
                    onReset={() => handleRangeReset(setAdSpend)}
                  />
                  <RangeCard
                    title="Bids"
                    label="%"
                    value={adBids}
                    onChange={([min, max]) => {
                      const currentValue = min === 0 ? max : min;
                      setAdBids(currentValue);
                    }}
                    min={-100}
                    max={100}
                    isWhatIf={true}
                    onReset={() => handleRangeReset(setAdBids)}
                  />
                  <RangeCard
                    title="Time in budget"
                    label="%"
                    value={timeInBudget}
                    onChange={([_, max]) => setTimeInBudget(max)}
                    min={0}
                    max={100}
                    isWhatIf={true}
                    onReset={() => handleRangeReset(setTimeInBudget)}
                  />
                  <RangeCard
                    title="Top of search"
                    label="%"
                    value={topOfSearch}
                    onChange={([_, max]) => setTopOfSearch(max)}
                    min={0}
                    max={100}
                    isWhatIf={true}
                    onReset={() => handleRangeReset(setTopOfSearch)}
                  />
                  <RangeCard
                    title="Product Pages"
                    value={productPages}
                    onChange={([_, max]) => setProductPages(max)}
                    min={0}
                    max={100}
                    isWhatIf={true}
                    onReset={() => handleRangeReset(setProductPages)}
                  />
                  <RangeCard
                    title="Rest of Search"
                    value={restOfSearch}
                    onChange={([_, max]) => setRestOfSearch(max)}
                    min={0}
                    max={100}
                    isWhatIf={true}
                    onReset={() => handleRangeReset(setRestOfSearch)}
                  />
                </div>
              )}
            </div>

            {/* DSP Spend Card - Only show if path includes DSP or Search Volume */}
            {shouldShowDspSpend && (
              <div className={styles.spendCard}>
                <h3>DSP Spend</h3>

                {!isDspSpendAdvancedOpen && (
                  <RangeCard
                    title="DSP Spend"
                    label="%"
                    value={dspSpend}
                    onChange={([_, max]) => setDspSpend(max)}
                    min={0}
                    max={100}
                    isWhatIf={true}
                    onReset={() => handleRangeReset(setDspSpend)}
                  />
                )}

                <div className={styles.advanced}>
                  <button
                    className={classNames(
                      styles.advanced__button,
                      isDspSpendAdvancedOpen && styles.active,
                    )}
                    onClick={() => {
                      setIsDspSpendAdvancedOpen(!isDspSpendAdvancedOpen);
                    }}
                  >
                    Advanced options <Arrow />
                  </button>
                </div>
                {isDspSpendAdvancedOpen && (
                  <div className={styles.cards}>
                    <RangeCard
                      title="DSP Spend"
                      label="%"
                      value={dspSpend}
                      onChange={([_, max]) => setDspSpend(max)}
                      min={0}
                      max={100}
                      isWhatIf={true}
                      onReset={() => handleRangeReset(setDspSpend)}
                    />
                    <RangeCard
                      title="DSP Retargeting"
                      label="%"
                      value={dspRetargeting}
                      onChange={([_, max]) => setDspRetargeting(max)}
                      min={0}
                      max={100}
                      isWhatIf={true}
                      onReset={() => handleRangeReset(setDspRetargeting)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.chart}>
        {/* Steps indicators */}
        <div className={styles.stepsContainer}>
          {["Step 1", "Step 2", "Step 3", "Step 4", "Step 5"].map(
            (step, index) => (
              <button
                key={step}
                className={classNames(
                  styles.stepIndicator,
                  styles[`step${index + 1}`],
                  selectedStep === index + 1 && styles.selectedStep,
                )}
                onClick={() => handleStepClick(index + 1)}
              >
                {step}
              </button>
            ),
          )}
        </div>
        <HorizontalChart
          data={chartData}
          seriesOptions={customSeriesOptions}
          tooltip={"customer"}
          onNodeClick={setSelectedNode}
        />
      </div>
    </div>
  );
};
